import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { EnvService } from './env.service';
import { SessionService } from './session.service';

/** Matches ppmapi `WrongCurrentPasswordMessage`. */
export const WRONG_CURRENT_PASSWORD_MESSAGE = 'WrongCurrentPassword';

/** Matches ppmapi `PasswordTooShortMessage`. */
export const PASSWORD_TOO_SHORT_MESSAGE = 'PasswordTooShort';

/** Matches ppmapi `ChangePasswordThrottledMessage`. */
export const CHANGE_PASSWORD_THROTTLED_MESSAGE = 'ChangePasswordThrottled';

export const MIN_PASSWORD_LENGTH = 8;

/** Matches ppmapi `EmailAddressInUseMessage`. */
export const EMAIL_ADDRESS_IN_USE_MESSAGE = 'EmailAddressInUse';

export type ChangePasswordErrorKey =
  | 'wrong-current-password'
  | 'password-too-short'
  | 'throttled'
  | 'request-failed';

export type ChangeEmailErrorKey =
  | 'email-in-use'
  | 'invalid-email'
  | 'request-failed';

/** Trim and lowercase a Pelican Account login email before compare or submit. */
export function normalizeLoginEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function mapChangePasswordError(error: unknown): ChangePasswordErrorKey {
  if (!(error instanceof HttpErrorResponse)) {
    return 'request-failed';
  }

  const message = error.error?.error?.message;
  if (error.status === 403 && message === WRONG_CURRENT_PASSWORD_MESSAGE) {
    return 'wrong-current-password';
  }
  if (error.status === 422 && message === PASSWORD_TOO_SHORT_MESSAGE) {
    return 'password-too-short';
  }
  if (error.status === 429 && message === CHANGE_PASSWORD_THROTTLED_MESSAGE) {
    return 'throttled';
  }

  return 'request-failed';
}

export function mapChangeEmailError(error: unknown): ChangeEmailErrorKey {
  if (!(error instanceof HttpErrorResponse)) {
    return 'request-failed';
  }

  const message = error.error?.error?.message;
  if (error.status === 409 && message === EMAIL_ADDRESS_IN_USE_MESSAGE) {
    return 'email-in-use';
  }
  if (error.status === 422) {
    return 'invalid-email';
  }

  return 'request-failed';
}

@Injectable({ providedIn: 'root' })
export class IdentityService {
  private readonly http = inject(HttpClient);
  private readonly envService = inject(EnvService);
  private readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const userId = this.sessionService.authUserId();
    if (userId === null) {
      return throwError(() => new Error('Not authenticated'));
    }

    return this.http.put<void>(`${this.envService.apiUrl}/users/${userId}/password`, {
      currentPassword,
      newPassword,
    });
  }

  changeEmail(email: string): Observable<string> {
    const userId = this.sessionService.authUserId();
    if (userId === null) {
      return throwError(() => new Error('Not authenticated'));
    }

    const normalizedEmail = normalizeLoginEmail(email);

    return this.http
      .put<{ accessToken: string }>(
        `${this.envService.apiUrl}/users/${userId}/email`,
        { email: normalizedEmail },
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          this.authService.applyLoginAccessToken(response.accessToken);
        }),
        map(() => normalizedEmail),
      );
  }
}
